import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, findCurrency, fmtMoney } from "@/lib/api";
import { SHARED } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Eye, Loader2, ShieldCheck } from "lucide-react";
import ComparisonGrid from "@/components/ComparisonGrid";

export default function SharedComparison() {
  const { comparisonId } = useParams();
  const [data, setData] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [dRes, cRes] = await Promise.all([
          api.get(`/compare/loan/shared/${comparisonId}`),
          api.get("/currencies"),
        ]);
        setData(dRes.data);
        setCurrencies(cRes.data.currencies);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Unable to load comparison");
      } finally {
        setLoading(false);
      }
    })();
  }, [comparisonId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading comparison…
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h1 className="font-display text-4xl tracking-tight">
          This comparison link isn’t valid
        </h1>
        <p className="mt-3 text-muted-foreground">
          The link may have been mistyped or removed. You can still draft your
          own comparison from scratch.
        </p>
        <Link to="/compare">
          <Button className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            Start a new comparison
          </Button>
        </Link>
      </div>
    );
  }

  const result = data.result;
  const currency = findCurrency(currencies, result.currency_code);

  return (
    <div
      data-testid={SHARED.page}
      className="max-w-7xl mx-auto px-6 lg:px-10 py-14"
    >
      {/* Shared header */}
      <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 flex flex-wrap items-start justify-between gap-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-secondary font-semibold">
            <ShieldCheck aria-hidden="true" className="w-3.5 h-3.5" />
            Shared comparison
          </div>
          <h1
            data-testid={SHARED.title}
            className="mt-3 font-display text-3xl lg:text-5xl tracking-tighter font-light leading-tight"
          >
            {result.business_name}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {result.industry_name} · {fmtMoney(result.loan_amount, currency)} ·{" "}
            {result.tenure_months} months · {result.columns.length}{" "}
            banking regions compared
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-xs text-muted-foreground">
          <span
            data-testid={SHARED.viewCount}
            className="inline-flex items-center gap-1 font-mono"
          >
            <Eye aria-hidden="true" className="w-3.5 h-3.5" />
            {data.view_count} view{data.view_count === 1 ? "" : "s"}
          </span>
          <span className="font-mono">
            Shared {new Date(data.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Read-only grid */}
      <div className="mt-10">
        <ComparisonGrid
          result={result}
          advisor={data.advisor_takeaway || ""}
          mode="readonly"
        />
      </div>

      {/* Sign-up CTA */}
      <div className="mt-12 rounded-3xl border border-primary/25 bg-primary text-primary-foreground p-8 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold">
            Powered by Imali
          </p>
          <h3 className="mt-2 font-display text-2xl lg:text-3xl tracking-tight font-light leading-tight">
            Draft your own comparison — free, no account needed.
          </h3>
          <p className="mt-2 text-primary-foreground/80 max-w-xl">
            Enter your borrower details, pick 2–4 regions, and get a
            lender-ready comparison across 12 banking systems in seconds.
          </p>
        </div>
        <Link to="/compare">
          <Button
            data-testid={SHARED.ctaDraft}
            size="lg"
            className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 px-7 h-12"
          >
            Try Imali
            <ArrowUpRight aria-hidden="true" className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
